// What a cliente owes its afianzadoras, and what they owe the cliente.
//
// Two figures the agente needs at a glance, and that nothing in the CRM exposed
// before the factura ledger existed:
//
//   saldoEnContra — primas billed and NOT paid. The cliente owes this to the
//                   afianzadora. Until it is paid the comisión cannot be
//                   collected either, so it is the agente's problem too.
//   saldoAFavor   — money the cliente already paid that a later reissue no
//                   longer covers. The afianzadora keeps it as credit (they
//                   rarely refund it, since the cliente will need another
//                   fianza). It offsets the next prima.
//
// Both are computed from the VIGENTE factura set: a superseded factura is not a
// debt, it was cancelled and reissued.

import { ensureFacturas, getFacturasVigentes, getEstadoFianza, ESTADO_FIANZA } from './fianzaEventos';
import { getEmisorNombre } from './calculations';

// After this many days without payment the afianzadora can report the cliente
// to buró de crédito. The aging list exists to keep facturas from getting there.
export const DIAS_BURO = 90;
export const DIAS_AVISO = 60;

const emptyTotals = () => ({ saldoEnContra: 0, saldoAFavor: 0, neto: 0, facturasPendientes: 0 });

/**
 * Balances for a single fianza.
 * @returns {{saldoEnContra:number, saldoAFavor:number, neto:number, facturasPendientes:number}}
 */
export function saldosDeFianza(fianza) {
  const totals = emptyTotals();
  // A terminal fianza's unpaid primas are not a live debt — it was cancelled,
  // anulada or replaced. Any credit it generated DOES survive.
  const terminal = getEstadoFianza(fianza) !== ESTADO_FIANZA.ACTIVA;

  getFacturasVigentes(ensureFacturas(fianza)).forEach((f) => {
    const prima = Number(f?.prima) || 0;
    if (!f?.pagada && !terminal && prima > 0) {
      totals.saldoEnContra += prima;
      totals.facturasPendientes += 1;
    }
    totals.saldoAFavor += Number(f?.saldoAFavor) || 0;
  });

  totals.neto = totals.saldoAFavor - totals.saldoEnContra;
  return totals;
}

/**
 * Balances across every fianza of a cliente, broken down by afianzadora — the
 * credit lives with a specific carrier and cannot be netted across them.
 *
 * @param {Array} contracts Contracts belonging to the cliente.
 * @returns {{
 *   saldoEnContra:number, saldoAFavor:number, neto:number, facturasPendientes:number,
 *   porAfianzadora: Array<{afianzadora:string, saldoEnContra:number, saldoAFavor:number, neto:number, facturasPendientes:number}>
 * }}
 */
export function saldosDeCliente(contracts) {
  const totals = { ...emptyTotals(), porAfianzadora: [] };
  const byAfianzadora = new Map();

  (contracts || []).forEach((contract) => {
    (contract?.fianzas || []).forEach((fianza) => {
      const s = saldosDeFianza(fianza);
      if (!s.saldoEnContra && !s.saldoAFavor) return;

      totals.saldoEnContra += s.saldoEnContra;
      totals.saldoAFavor += s.saldoAFavor;
      totals.facturasPendientes += s.facturasPendientes;

      const key = getEmisorNombre(fianza) || 'Sin afianzadora';
      const entry = byAfianzadora.get(key) || { afianzadora: key, ...emptyTotals() };
      entry.saldoEnContra += s.saldoEnContra;
      entry.saldoAFavor += s.saldoAFavor;
      entry.facturasPendientes += s.facturasPendientes;
      entry.neto = entry.saldoAFavor - entry.saldoEnContra;
      byAfianzadora.set(key, entry);
    });
  });

  totals.neto = totals.saldoAFavor - totals.saldoEnContra;
  totals.porAfianzadora = Array.from(byAfianzadora.values())
    .sort((a, b) => b.saldoEnContra - a.saldoEnContra);
  return totals;
}

/**
 * Every unpaid vigente factura of the cliente, aged from its emission date —
 * oldest first, because those are the ones about to hit the buró threshold.
 *
 * @param {Array} contracts Contracts belonging to the cliente.
 * @param {number} [nowMs] Injectable clock for tests.
 * @returns {Array<{
 *   fianzaId:string, numeroFianza:string, afianzadora:string, facturaId:string,
 *   prima:number, fechaMs:number|null, dias:number|null,
 *   enRiesgoBuro:boolean, enAviso:boolean
 * }>}
 */
export function facturasPorPagar(contracts, nowMs) {
  const now = nowMs ?? Date.now();
  const rows = [];

  (contracts || []).forEach((contract) => {
    (contract?.fianzas || []).forEach((fianza) => {
      // Terminal fianzas' unpaid primas are not a live debt (same rule as saldos).
      if (getEstadoFianza(fianza) !== ESTADO_FIANZA.ACTIVA) return;

      getFacturasVigentes(ensureFacturas(fianza)).forEach((f) => {
        const prima = Number(f?.prima) || 0;
        if (f?.pagada || prima <= 0) return;

        const fechaMs = Number(f?.fecha) || null;
        const dias = fechaMs ? Math.floor((now - fechaMs) / 86400000) : null;
        rows.push({
          fianzaId: fianza.id,
          contratoId: contract.id,
          clientId: contract.clientId || null,
          convenioId: contract.convenioId || null,
          numeroFianza: fianza.numeroFianza || fianza.xmlFianza?.numeroFianza || 'Sin número',
          afianzadora: getEmisorNombre(fianza) || 'Sin afianzadora',
          facturaId: f?.facturaId || f?.id || null,
          prima,
          fechaMs,
          dias,
          enRiesgoBuro: dias !== null && dias >= DIAS_BURO,
          enAviso: dias !== null && dias >= DIAS_AVISO && dias < DIAS_BURO,
        });
      });
    });
  });

  // Oldest debt first; facturas with no date sink to the bottom.
  return rows.sort((a, b) => (b.dias ?? -1) - (a.dias ?? -1));
}
